import { useEffect, useMemo, useState } from 'react';
import { isWindReadingCurrent, padPilotNumber } from '@aero-judge/utils';
import { motion } from 'framer-motion';
import { Search } from 'lucide-react';
import { Input, LeaderboardTable } from '@aero-judge/ui';
import { useCompetition, useLatestWind, useResults, toLeaderboardEntries } from '../hooks/useCompetition';
import { isCompetitionCompleted } from '../lib/competitionStatus';

interface LiveLeaderboardProps {
  title?: string;
  showHeading?: boolean;
  showSearch?: boolean;
  /** Controlled search. When set with `showSearch` false, the parent renders the field. */
  query?: string;
  onQueryChange?: (value: string) => void;
  maxRows?: number;
}

export function LiveLeaderboard({
  title,
  showHeading = true,
  showSearch = true,
  query,
  onQueryChange,
  maxRows,
}: LiveLeaderboardProps) {
  const { data: competition } = useCompetition();
  const { data: results, isLoading, error } = useResults('OVERALL');
  const { data: wind } = useLatestWind();
  const [internalSearch, setInternalSearch] = useState('');
  const search = query ?? internalSearch;
  const setSearch = onQueryChange ?? setInternalSearch;
  const completed = isCompetitionCompleted(competition?.status);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 15_000);
    return () => window.clearInterval(id);
  }, []);
  const windVisible =
    !!wind &&
    isWindReadingCurrent(
      wind.recordedAt,
      competition?.settings?.windDisplayMaxAgeMinutes ?? 30,
      now,
    );
  const heading = title ?? (completed ? 'Final Results' : 'Live Leaderboard');

  const entries = useMemo(() => {
    const all = toLeaderboardEntries(results);
    if (!search.trim()) return maxRows ? all.slice(0, maxRows) : all;
    const q = search.toLowerCase();
    const filtered = all.filter(
      (e) =>
        `${e.firstName} ${e.lastName}`.toLowerCase().includes(q) ||
        String(e.pilotNumber).includes(q) ||
        (e.pilotNumber != null && padPilotNumber(e.pilotNumber).includes(q)) ||
        e.countryCode?.toLowerCase().includes(q) ||
        e.countryCode2?.toLowerCase().includes(q),
    );
    return maxRows ? filtered.slice(0, maxRows) : filtered;
  }, [results, search, maxRows]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-sky-400 border-t-transparent" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-8 text-center text-red-300">
        Unable to load results. Please try again later.
      </div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-3">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        {showHeading ? (
          <h2 className="font-display text-3xl font-bold text-foreground">{heading}</h2>
        ) : (
          <span className="sr-only">{heading}</span>
        )}
        {showSearch && (
          <div className={showHeading ? 'relative max-w-sm' : 'relative ml-auto max-w-sm'}>
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search pilots…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="border-border bg-card pl-10 text-foreground placeholder:text-muted-foreground"
            />
          </div>
        )}
      </div>

      <div className="-mx-4 overflow-x-auto border-y border-border bg-card backdrop-blur-sm sm:mx-0 sm:rounded-xl sm:border">
        <LeaderboardTable
          entries={entries}
          showRoundScores
          roundNumbers={results?.roundNumbers}
          showBullseyes={false}
          showRounds={false}
          highlightPodium
          className="min-w-max [&_th]:h-8 [&_th]:border-border [&_th]:px-1 [&_th]:text-xs [&_th]:text-muted-foreground [&_td]:border-border/70 [&_td]:px-1 [&_td]:py-2 sm:[&_th]:px-3 sm:[&_td]:px-3 [&_tr]:border-border/70 [&_td]:text-foreground"
        />
      </div>

      <p className="text-center text-sm text-muted-foreground">
        {completed
          ? 'Competition completed · Official final standings'
          : results?.official
            ? 'Official results · Updated live'
            : 'Provisional results · Updated live'}
        {!completed && windVisible ? (
          <span className="mt-1 block font-mono text-muted-foreground">
            Wind {wind.speedMs.toFixed(1)} m/s · {Math.round(wind.directionDeg)}°
          </span>
        ) : null}
      </p>
    </motion.div>
  );
}
